import { Keyboard, Moon, PanelLeft, Palette, RefreshCw, RotateCw } from 'lucide-react';
import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useRegisterCommands } from '../../lib/commands.ts';
import type { Command } from '../../lib/commands.ts';
import { startData } from '../../lib/data.ts';
import { setMockPreference } from '../../lib/mock.ts';
import { NAV, SECONDARY_NAV } from '../../lib/nav.ts';
import { usePrefs } from '../../lib/prefs.ts';
import { routeSwitch } from '../../lib/routeSwitch.ts';
import { useGlobalShortcuts } from '../../lib/shortcuts.ts';
import { useThemeColor } from '../../lib/installedApp.ts';
import { useConnection } from '../../lib/store.ts';
import { Kbd } from '@orgus/ui';
import { Toaster } from '@orgus/ui';
import { TooltipProvider } from '@orgus/ui';
import { RunCommandHost } from '../../features/jobs/RunCommandHost.tsx';
import { ProjectHealthHosts } from '../../features/project-health/Hosts.tsx';
import { ProjectAutonomyHost } from '../../features/autonomy/ProjectAutonomyDialog.tsx';
import { AuthGate } from '../../features/auth/AuthGate.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { Rail } from './Rail.tsx';
import { ShortcutsHelp } from './ShortcutsHelp.tsx';
import { TitleBar } from './TitleBar.tsx';
import { TopBar } from './TopBar.tsx';
import { useOriginTracking } from './useOrigin.ts';
import { StaleHubNotice } from './StaleHubBanner.tsx';
import { PhoneHandOff } from './PhoneHandOff.tsx';
import { StaleBanner } from './PageBody.tsx';
import { useHubDown } from '../../lib/useHubDown.ts';
import { cn } from '@orgus/ui';
import { NotifyFallback } from '../../features/notify/NotifyFallback.tsx';
import { AnswerSheet } from '../../features/approvals/AnswerSheet.tsx';
import { WatchNotices } from '../../features/watch/WatchNotices.tsx';
import { reloadPage } from '../../features/installed-app/ReloadButton.tsx';
import { useMissionsPauseCommands } from '../../features/missions/PauseControl.tsx';

export interface RouteHandle {
  /** Top bar title for routes outside the rail. */
  title?: string;
}

/** Navigation and general commands, registered for the whole app. */
function useShellCommands() {
  const navigate = useNavigate();
  const { mode } = useConnection();
  const cmds = useMemo<Command[]>(
    () => [
      ...[...NAV, ...SECONDARY_NAV].map(
        (n, i): Command => ({
          id: `nav.${n.to}`,
          title: `Go to ${n.label}`,
          group: 'Navigation',
          icon: n.icon,
          shortcut: ['g', n.key],
          shortcutThen: true,
          keywords: n.keywords,
          order: i,
          perform: () => navigate(n.to),
        }),
      ),
      {
        id: 'general.theme',
        title: 'Switch theme',
        group: 'General',
        icon: Moon,
        keywords: ['dark', 'light', 'appearance'],
        perform: () => usePrefs.getState().toggleTheme(),
      },
      {
        id: 'general.rail',
        title: 'Collapse or expand the sidebar',
        group: 'General',
        icon: PanelLeft,
        shortcut: ['['],
        perform: () => usePrefs.getState().toggleRail(),
      },
      {
        id: 'general.shortcuts',
        title: 'Show keyboard shortcuts',
        group: 'General',
        icon: Keyboard,
        shortcut: ['?'],
        perform: () => usePrefs.getState().setHelpOpen(true),
      },
      {
        id: 'general.design',
        title: 'Open the design system',
        group: 'General',
        icon: Palette,
        keywords: ['components', 'primitives', 'tokens'],
        perform: () => navigate('/design'),
      },
      {
        id: 'general.reload',
        title: 'Reload page',
        group: 'General',
        icon: RotateCw,
        keywords: ['refresh', 'reload', 'update'],
        perform: reloadPage,
      },
      {
        id: 'general.data',
        title: mode === 'mock' ? 'Connect to the hub' : 'Show demo data',
        subtitle: mode === 'mock' ? 'Leave demo mode' : 'Simulated machines and sessions',
        group: 'General',
        icon: RefreshCw,
        keywords: ['mock', 'demo', 'live'],
        perform: () => {
          setMockPreference(mode !== 'mock');
          startData({ mock: mode !== 'mock' });
        },
      },
    ],
    [navigate, mode],
  );
  useRegisterCommands(cmds, [cmds]);
  // ADR 0103: Pause all missions from anywhere, not only the Missions page.
  useMissionsPauseCommands();
}

/** Shown after pressing g: the keys that complete the jump. */
function SequenceHint({ visible }: { visible: boolean }) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8, transition: { duration: 0.12 } }}
          transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
          role="status"
          className="pointer-events-none fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3 rounded-lg border border-border-strong bg-raised px-3 py-2 text-sm text-fg-muted shadow-overlay"
        >
          <Kbd keys={['g']} />
          <span className="text-fg-subtle">then</span>
          {NAV.map((n) => (
            <span key={n.key} className="flex items-center gap-1">
              <Kbd keys={[n.key]} />
              <span>{n.label}</span>
            </span>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Marks the root data-live-ready a moment after the first state has painted. Arrivals
 * (motion-arrive) animate only under it, so nothing moves on page load (motion.md L1).
 */
function useLiveReady() {
  const { phase } = useConnection();
  useEffect(() => {
    if (phase !== 'ready') return;
    const t = window.setTimeout(() => document.documentElement.setAttribute('data-live-ready', ''), 1200);
    return () => window.clearTimeout(t);
  }, [phase]);
}

/**
 * The page for the router's location is on screen: live updates may go in again (lib/routeSwitch.ts).
 * A component of its own, so a navigation re-renders nothing else in the shell.
 */
function RouteSwitchEnd() {
  const { key } = useLocation();
  useLayoutEffect(() => routeSwitch.end(), [key]);
  return null;
}

/** Rail, top bar, routed page, and the global overlays (palette, shortcuts, toasts). */
export function AppShell() {
  const { pendingG } = useGlobalShortcuts();
  useShellCommands();
  useOriginTracking();
  // With the hub gone the page is the last known state: grey it out, so no colour claims "live".
  const down = useHubDown();
  useLiveReady();
  useThemeColor();

  return (
    <MotionConfig reducedMotion="user">
      <TooltipProvider delayDuration={300} skipDelayDuration={200}>
        <AuthGate>
        <div className="flex h-full w-full flex-col overflow-hidden bg-rail text-fg">
          <a
            href="#main"
            className="sr-only z-[70] rounded-md bg-raised px-3 py-2 text-base focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
          >
            Skip to content
          </a>
          {/* The installed desktop app's title bar: one strip over the rail and the content sheet. */}
          <TitleBar />
          <div className="flex min-h-0 flex-1">
            <Rail />
            {/* The content sheet: one raised plane on the frame, with the brand atmosphere at its top. */}
            <div className="atmosphere relative my-2 mr-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-overlay border border-border bg-bg shadow-elevation-flat wco:mt-0 max-md:m-0 max-md:rounded-none max-md:border-0">
              <TopBar />
              {/* Notices publish into the top bar's notices item; only a page-blocking error is a banner. */}
              <StaleHubNotice />
              <NotifyFallback />
              <WatchNotices />
              <StaleBanner />
              <main id="main" tabIndex={-1} data-stale={down || undefined} className={cn('min-h-0 flex-1 overflow-y-auto outline-none', down && 'grayscale')}>
                <Outlet />
                <RouteSwitchEnd />
              </main>
            </div>
          </div>
        </div>
        <PhoneHandOff />
        </AuthGate>
        <CommandPalette />
        <RunCommandHost />
        <ProjectHealthHosts />
        <ProjectAutonomyHost />
        <AnswerSheet />
        <ShortcutsHelp />
        <SequenceHint visible={pendingG} />
        <Toaster />
      </TooltipProvider>
    </MotionConfig>
  );
}
